import { Alert, Card, showToast } from '@unmind/design-system';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import {
  SessionDetails,
  SessionDetailsIcon,
  parseTimeString,
} from 'Book/shared';
import { AppointmentMode } from '__generated__/globalTypes';
import { initialFormState } from '../Form';
import { Wrapper } from './Summary.styles';
import { ISummaryProps } from './types';

export default ({ details, dispatch, formData, service }: ISummaryProps) => {
  const { appointmentId, counsellor, submittedForm } = details || {};

  const { t } = useTranslation('book', {
    keyPrefix: 'service.form',
  });

  if (!appointmentId || !formData[2]?.date) {
    return (
      <Wrapper className="wrapper">
        <Alert
          className="card error"
          title={t('summary.error')}
          variant="error"
          size="small"
        />
      </Wrapper>
    );
  }

  const mode = service?.appointmentTypes.find(
    a => a.mode === formData[1].appointmentMode.toUpperCase(),
  );

  // Add the time to the start date, so we can calculate the end date
  const startTimeParts = parseTimeString(formData[4]?.time)
    ?.split(':')
    .map(Number);
  const startDateTime = new Date(formData[2].date).setUTCHours(
    startTimeParts?.[0] ?? 0,
    startTimeParts?.[1] ?? 0,
    startTimeParts?.[2] ?? 0,
  );
  const endDateTime = new Date(startDateTime);
  endDateTime.setUTCMinutes(
    endDateTime.getUTCMinutes() + (mode?.durationMinutes || 30),
  );

  return (
    <Wrapper className={`wrapper ${submittedForm ? '' : 'low-detail'}`}>
      {submittedForm ? (
        <Alert
          className="card success"
          variant="success"
          title={t('summary.success.title')}
          body={t('summary.success.text', { email: formData['0'].email })}
          size="small"
        />
      ) : (
        <></>
      )}

      <SessionDetails
        appointment={{
          id: appointmentId || '',
          type: formData[1].appointmentMode,
          onCancel: () => {
            dispatch([
              { type: 'step', value: 0 },
              { type: 'answers', value: initialFormState.answers },
              { type: 'details', value: initialFormState.details },
            ]);
            showToast(t('toast'));
          },
          timeZone: formData[1].timeZone,
        }}
        canCancel={{ button: 'bottom' }}
        counsellor={
          counsellor
            ? {
                name: `${counsellor?.firstName} ${counsellor?.lastName}`.trim(),
                picture: counsellor?.picture,
              }
            : undefined
        }
        dates={{
          startDate: formData[2]?.date,
          startTime: formData[4]?.time,
          endDateTime,
        }}
        type={service}
        showAddToCalendar
        trailingContent={
          details?.submittedForm ? undefined : (
            <SessionDetailsIcon service={service.serviceSlug} />
          )
        }
      />
      <Card element="aside" tone="nested" styling="no-border">
        <h2>{t('summary.whats-next.title')}</h2>
        <ul>
          {formData[1].appointmentMode === AppointmentMode.PHONE ? (
            <li>
              <Trans
                t={t}
                i18nKey="summary.whats-next.phone"
                components={{
                  counsellor: <strong />,
                }}
                values={{
                  counsellor:
                    counsellor?.firstName ||
                    t('confirmDetails.counsellorName.fallback.startSentence'),
                }}
              />
            </li>
          ) : (
            <li>{t('summary.whats-next.video')}</li>
          )}
          <Trans
            t={t}
            i18nKey="summary.whats-next.text"
            components={{
              li: <li />,
            }}
          />
        </ul>
      </Card>
    </Wrapper>
  );
};
